import React, { useMemo, forwardRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

// Color palette for charts
const chartColors = [
  '#E36250',
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
];

const ChartRenderer = forwardRef(({ type, data, mappings, options, comparisonData }, ref) => {
  const chartOption = useMemo(() => {
    if (!data || data.length === 0) return null;

    const baseOptions = {
      tooltip: options.showTooltip !== false ? { trigger: type === 'pie' ? 'item' : 'axis' } : undefined,
      legend: options.showLegend ? { bottom: 0, type: 'scroll' } : undefined,
      color: options.colors || chartColors,
      grid: { left: '3%', right: '4%', bottom: options.showLegend ? '15%' : '3%', top: '10%', containLabel: true },
    };

    switch (type) {
      case 'bar':
        return {
          ...baseOptions,
          xAxis: {
            type: 'category',
            data: data.map((d) => d[mappings.x || '']),
            axisLabel: { rotate: data.length > 6 ? 45 : 0 },
          },
          yAxis: { type: 'value' },
          series: [
            {
              type: 'bar',
              data: data.map((d) => d[mappings.y || '']),
              itemStyle: { borderRadius: [4, 4, 0, 0] },
            },
          ],
        };

      case 'line':
        const lineSeries = mappings.series || [mappings.y || ''];
        return {
          ...baseOptions,
          xAxis: {
            type: 'category',
            data: data.map((d) => d[mappings.x || '']),
            boundaryGap: false,
          },
          yAxis: { type: 'value' },
          series: lineSeries.map((seriesKey) => {
            const formattedName = seriesKey
              .replace(/_/g, ' ')
              .replace(/\b\w/g, l => l.toUpperCase());
            return {
              name: formattedName,
              type: 'line',
              data: data.map((d) => d[seriesKey] || 0),
              smooth: true,
              symbol: 'circle',
              symbolSize: 6,
              lineStyle: { width: 2 },
            };
          }),
        };

      case 'area':
        const areaSeries = mappings.series || [mappings.y || ''];
        return {
          ...baseOptions,
          xAxis: {
            type: 'category',
            data: data.map((d) => d[mappings.x || '']),
            boundaryGap: false,
          },
          yAxis: { 
            type: 'value',
            axisLabel: {
              formatter: (value) => {
                if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
                return `$${value}`;
              }
            }
          },
          series: areaSeries.map((seriesKey) => {
            const formattedName = seriesKey
              .replace(/_/g, ' ')
              .replace(/\b\w/g, l => l.toUpperCase());
            return {
              name: formattedName,
              type: 'line',
              data: data.map((d) => d[seriesKey] || 0),
              smooth: true,
              areaStyle: { opacity: 0.3 },
              emphasis: { focus: 'series' },
            };
          }),
        };

      case 'pie':
        const pieData = data
          .filter(d => d[mappings.value || ''] > 0)
          .map((d) => ({
            name: d[mappings.label || ''] || 'Unknown',
            value: d[mappings.value || ''] || 0,
          }));
        return {
          ...baseOptions,
          tooltip: { 
            trigger: 'item', 
            formatter: (params) => {
              const value = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(params.value);
              return `${params.name}: ${value} (${params.percent}%)`;
            }
          },
          series: [
            {
              type: 'pie',
              radius: ['40%', '70%'],
              avoidLabelOverlap: true,
              itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 2 },
              label: { show: true, formatter: '{b}: {d}%' },
              emphasis: {
                label: { show: true, fontSize: 14, fontWeight: 'bold' },
              },
              data: pieData,
            },
          ],
        };

      case 'funnel':
        return {
          ...baseOptions,
          tooltip: { trigger: 'item', formatter: '{b}: {c}' },
          series: [
            {
              type: 'funnel',
              left: '10%',
              top: 40,
              bottom: 40,
              width: '80%',
              sort: 'descending',
              gap: 2,
              label: { show: true, position: 'inside', formatter: '{b}: {c}' },
              itemStyle: { borderColor: '#fff', borderWidth: 1 },
              emphasis: { label: { fontSize: 14 } },
              data: data.map((d) => ({
                name: d[mappings.label || ''],
                value: d[mappings.value || ''],
              })),
            },
          ],
        };

      case 'combo':
        const comboSeries = mappings.series || [mappings.y || ''];
        return {
          ...baseOptions,
          xAxis: {
            type: 'category',
            data: data.map((d) => d[mappings.x || '']),
            axisLabel: { rotate: data.length > 5 ? 45 : 0 },
          },
          yAxis: [
            { type: 'value', name: 'Count', position: 'left' },
            { type: 'value', name: 'Amount ($)', position: 'right' },
          ],
          series: comboSeries.map((seriesKey, index) => {
            const isAmount = seriesKey.includes('amount');
            const formattedName = seriesKey
              .replace(/_/g, ' ')
              .replace(/\b\w/g, l => l.toUpperCase());
            return {
              name: formattedName,
              type: isAmount ? 'line' : 'bar',
              yAxisIndex: isAmount ? 1 : 0,
              data: data.map((d) => d[seriesKey] || 0),
              smooth: isAmount,
              itemStyle: !isAmount ? { borderRadius: [4, 4, 0, 0] } : undefined,
            };
          }),
        };

      case 'scalar':
        return null; // Scalar is handled separately

      default:
        return null;
    }
  }, [type, data, mappings, options]);

  // Render scalar/KPI type
  if (type === 'scalar') {
    const value = data[0]?.[mappings.label || ''] || 0;
    
    // Check if value should be formatted as currency or number
    const isCurrency = mappings.label?.includes('amount') || mappings.label?.includes('payable');
    const formattedValue = isCurrency 
      ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value)
      : new Intl.NumberFormat('en-US').format(value);

    const renderComparisonBadge = () => {
      if (!comparisonData) return null;
      const { percentChange } = comparisonData;
      const isPositive = percentChange > 0;
      const isNeutral = percentChange === 0;

      return (
        <div
          className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${
            isNeutral
              ? 'bg-gray-100 text-gray-600'
              : isPositive
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}
        >
          {isNeutral ? (
            <Minus size={14} />
          ) : isPositive ? (
            <TrendingUp size={14} />
          ) : (
            <TrendingDown size={14} />
          )}
          <span>{Math.abs(percentChange).toFixed(1)}%</span>
        </div>
      );
    };

    return (
      <div className="flex flex-col items-center justify-center h-full">
        <span className="text-4xl font-bold text-gray-800">{formattedValue}</span>
        <span className="text-sm text-gray-500 mt-1">
          {isCurrency ? 'Total Amount' : 'Total Invoices'}
        </span>
        {comparisonData && (
          <div className="mt-3 flex items-center gap-2">
            {renderComparisonBadge()}
            <span className="text-sm text-gray-500">vs previous period</span>
          </div>
        )}
      </div>
    );
  }

  if (!chartOption) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        No data available
      </div>
    );
  }

  return (
    <ReactECharts
      ref={ref}
      option={chartOption}
      style={{ height: '100%', width: '100%' }}
      opts={{ renderer: 'canvas' }}
      notMerge={true}
    />
  );
});

ChartRenderer.displayName = 'ChartRenderer';

export default ChartRenderer;
